import { zodResolver } from '@hookform/resolvers/zod';
import DeleteIcon from '@mui/icons-material/Delete';
import PasswordIcon from '@mui/icons-material/Password';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { RoleEnum, UserUpdateDtoSchema } from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { useUpdatePasswordDialog } from '_lib/hooks/UpdatePasswordDialogHook';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthUser } from '../auth/auth.types';
import { AuthContext } from '../auth/authContext';
import { SystemUser } from '../auth/permissionHelpers';
import { NavigationBlocker } from '../NavigationBlocker/NavigationBlocker';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';

import { UserForm } from './UserForm';

type UserUpdateProps = {
  userId: string;
  authUser: AuthUser | undefined;
  isFetchingFromProps?: boolean;
};

export const UserUpdate = (props: UserUpdateProps) => {
  const { userId, authUser, isFetchingFromProps } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const updatePasswordDialog = useUpdatePasswordDialog();

  const api = useContext(ApiContext);
  const { authUserCacheResource } = useContext(AuthContext);

  const userPutMutation = api.users.usePut();
  const userDeleteMutation = api.users.useDelete();
  const userReactivateMutation = api.users.useReactivate();

  const userQuery = api.users.useGetById({ id: userId });
  const userData = userQuery?.data;

  const supervisorsQuery = api.users.useUnpagedList(
    { params: { isSupervisor: true } },
    { enabled: userData && !SystemUser({ user: userData }) }
  );

  const userFormContext = useForm({
    resolver: zodResolver(UserUpdateDtoSchema.required()),
    mode: 'onChange',
    shouldFocusError: true
  });

  useEffect(() => {
    if (userData) {
      userFormContext.reset(userData);
    }
  }, [userData, userFormContext]);

  const handleSave = apiSubmitHandler(
    userFormContext.handleSubmit,
    userFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const result = await userPutMutation.put({ id: userId, data });
        if (result.id === authUser?.id) {
          await authUserCacheResource.set(result).catch(() => {});
        }
        userFormContext.reset(result);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  const handleDeactivate = async () => {
    try {
      await userDeleteMutation.deleteItem({ id: userId });
      enqueueSnackbar('User deactivated');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
      }
    }
  };

  const handleReactivate = async () => {
    try {
      await userReactivateMutation.reactivate({ id: userId });
      enqueueSnackbar('User reactivated');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
      }
    }
  };

  if (userQuery.error) {
    return <RouteErrorBoundary disableAppBar errorTitle='Error Fetching User' />;
  }

  const isFetchingOrPending =
    userQuery.isFetching || supervisorsQuery.isPending || isFetchingFromProps || userPutMutation.isPending;

  const isEditingOwnProfile = userData?.id === authUser?.id;

  if (!isFetchingOrPending && !userData) {
    return <RouteErrorBoundary errorTitle='User Not Found' />;
  }

  return (
    <>
      <NavigationBlocker getBlockCondition={() => Object.keys(userFormContext.formState.dirtyFields).length > 0} />
      <ActionBar
        actions={
          <>
            <Button
              disableFocusRipple
              text='Update Password'
              variant='text'
              onClick={async () => {
                if (userData) await updatePasswordDialog.show({ user: userData });
              }}
              startIcon={<PasswordIcon />}
              sx={{ textTransform: 'none' }}
              disabled={
                isFetchingOrPending ||
                !userData ||
                !userData.isActive ||
                (authUser?.role === RoleEnum.USER && userData.role !== RoleEnum.USER)
              }
            />
            {!isEditingOwnProfile && (
              <Button
                disableFocusRipple
                text={userData?.isActive ? 'Deactivate' : 'Reactivate'}
                variant='text'
                onClick={userData?.isActive ? handleDeactivate : handleReactivate}
                startIcon={userData?.isActive ? <DeleteIcon /> : <SettingsBackupRestoreIcon />}
                sx={{ textTransform: 'none' }}
                disabled={isFetchingOrPending || (authUser?.role === RoleEnum.USER && userData?.role !== RoleEnum.USER)}
              />
            )}
          </>
        }
      />
      <Divider />
      <FlexBox
        sx={(theme) => ({
          backgroundColor: theme.palette.background.default,
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        })}>
        <BackdropSpinner open={isFetchingOrPending} />
        {!!authUser && !!Object.keys(userFormContext.getValues()).length && (
          <>
            <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
              {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
              {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
              {!userQuery.isFetching && userData?.isActive === false && (
                <WarningAlert>
                  <Typography>User is inactive</Typography>
                </WarningAlert>
              )}
              <UserForm
                userFormContext={userFormContext}
                authUser={authUser}
                isInactive={!userData?.isActive}
                isEditingOwnProfile={isEditingOwnProfile}
                supervisors={supervisorsQuery.data}
              />
            </Stack>
            {isOverflowing && <Divider />}
            <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
              <Button
                disabled={!userData?.isActive || (authUser?.role === RoleEnum.USER && userData?.role !== RoleEnum.USER)}
                onClick={handleSave}
                text='Save'
                variant='contained'
                size='small'
                sx={{ textTransform: 'none' }}
              />
            </FlexBox>
          </>
        )}
      </FlexBox>
      {updatePasswordDialog.element}
    </>
  );
};
